import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { isPlatform } from '@ionic/vue';
import md5 from 'md5';
import { getPlatform } from '@/utils/misc';

const memCache = new Map<string, string>();
const platform = getPlatform();
const cacheDirectory = Directory.Data;

const getCachePath = (url: string) => {
  const ext = (url.split('.').pop() || 'jpg').split(/[?#]/)[0] || 'jpg';
  return `cache/${md5(url)}.${ext}`;
};
// LRU: get moves entry to end
const getCache = (url: string) => {
  const path = memCache.get(url);
  if (path !== undefined) {
    memCache.delete(url);
    memCache.set(url, path);
  }
  return path;
};

const addCache = (url: string, path: string) => {
  if (memCache.size >= 500) memCache.delete(memCache.keys().next().value!);
  memCache.set(url, path);
  return path;
};

class CachedImage {
  // Sync lookup for memCache, returns undefined if not cached
  getCached(url: string): string | undefined {
    if (!url.match(/^https?:\/\//)) return url;
    return getCache(url);
  }
  async get(url: string) {
    if (!url.match(/^https?:\/\//)) return url;
    const cached = getCache(url);
    if (cached !== undefined) return cached;

    // Electron: check file cache via window.__cacheDir
    if (platform === 'electron') {
      const fileName = url.match(/\/([^/]+)$/)?.[1];
      const cachePath = fileName && window.__cacheDir ? `${window.__cacheDir}/${fileName}`.replace(/\\/g, '/') : null;
      return cachePath && window.electron?.fileExist(cachePath) ? addCache(url, cachePath) : url;
    }

    if (!isPlatform('capacitor')) return url;

    const cachePath = getCachePath(url);
    try { await Filesystem.stat({ path: cachePath, directory: cacheDirectory }); }
    catch {
      try { await Filesystem.stat({ path: 'cache', directory: cacheDirectory }); }
      catch { await Filesystem.mkdir({ path: 'cache', directory: cacheDirectory, recursive: true }); }
      await Filesystem.downloadFile({ url, path: cachePath, directory: cacheDirectory });
    }

    const localUri = Capacitor.convertFileSrc((await Filesystem.getUri({ path: cachePath, directory: cacheDirectory })).uri);
    return platform === 'android' ? addCache(url, localUri) : localUri;
  }

  async has(url: string): Promise<boolean> {
    if (platform === 'electron') {
      const fileName = url.match(/\/([^/]+)$/)?.[1];
      return !!fileName && !!window.__cacheDir && !!window.electron?.fileExist(`${window.__cacheDir}/${fileName}`);
    }
    try { await Filesystem.stat({ path: getCachePath(url), directory: cacheDirectory }); return true; }
    catch { return false; }
  }
}
// Singleton to avoid multiple instances sharing the same module-level Map
export const cachedImage = new CachedImage();
